/**
 * Sport → Circuit mapping.
 * The main dropdown lists SPORTS in the exact order the user specified.
 * Each sport has 3 top travel/club circuits, then "Independent" and "Other".
 * Basketball keeps its previous circuit list (EYBL, Gauntlet, UAA, AAU, Independent).
 */

export const SPORTS = [
    "Basketball",
    "Soccer",
    "Baseball",
    "Volleyball",
    "Softball",
    "Football",
    "Flag Football",
    "Mixed Martial Arts",
    "Ice Hockey",
    "Gymnastics",
    "Swimming",
    "Cheer",
    "Lacrosse",
];

export const DEFAULT_SPORT = "Basketball";

// Each sport → its circuit dropdown options in display order.
// "Independent" and "Other" are appended for every non-basketball sport.
export const CIRCUITS_BY_SPORT = {
    Basketball: ["NIKE EYBL", "Gauntlet", "UAA", "AAU", "Independent"],
    Soccer: ["ECNL", "MLS NEXT", "GA (Girls Academy)", "Independent", "Other"],
    Baseball: ["Perfect Game", "USSSA Baseball", "Prep Baseball Report (PBR)", "Independent", "Other"],
    Volleyball: ["USA Volleyball", "AAU Volleyball", "JVA", "Independent", "Other"],
    Softball: ["PGF", "USSSA Fastpitch", "USA Softball", "Independent", "Other"],
    Football: ["Pylon 7v7", "Nike TOC", "Under Armour NextGen", "Independent", "Other"],
    "Flag Football": ["NFL FLAG", "USA Football", "AAU Flag Football", "Independent", "Other"],
    "Mixed Martial Arts": ["USA MMA", "IMMAF", "AAU Pankration", "Independent", "Other"],
    Lacrosse: ["3d Lacrosse", "Under Armour Command", "Adrenaline", "Independent", "Other"],
    "Ice Hockey": ["USA Hockey Tier 1", "HPHL", "T1EHL", "Independent", "Other"],
    Gymnastics: ["USAG Development Program", "USAG Xcel", "Elite / National Team", "Independent", "Other"],
    Swimming: ["USA Swimming", "YMCA Swim", "ISCA", "Independent", "Other"],
    Cheer: ["USASF", "Varsity All-Star", "NCA", "Independent", "Other"],
};

export function circuitsForSport(sport) {
    return CIRCUITS_BY_SPORT[sport] || CIRCUITS_BY_SPORT[DEFAULT_SPORT];
}

// Age groups per sport. Soccer goes up to 19U; every other sport tops out at 18U.
export function agesForSport(sport) {
    const max = sport === "Soccer" ? 19 : 18;
    const out = [];
    for (let i = 7; i <= max; i++) out.push(`${i}U`);
    return out.filter((a) => a !== "18U");
}

// Universal age ordering (7U..19U) for collapsing age ranges regardless of sport.
export const ALL_AGES = Array.from({ length: 13 }, (_, i) => `${i + 7}U`);


// Elementary → high school grades used for the Grade multi-select filter and Add forms.
export const GRADES = ["2nd", "3rd", "4th", "5th", "6th", "7th", "8th", "9th", "10th", "11th", "12th"];

// Collapse a set of values into contiguous ranges, returned as an ARRAY of labels.
// e.g. ["3rd","4th","5th","6th"] -> ["3rd-6th"];  ["3rd","4th","6th"] -> ["3rd-4th","6th"].
// `order` is the canonical ordering (GRADES or agesForSport(sport)).
export function collapseRanges(values, order) {
    const idx = (values || []).map((v) => order.indexOf(v)).filter((i) => i >= 0).sort((a, b) => a - b);
    if (!idx.length) return [];
    const runs = [];
    let start = idx[0], prev = idx[0];
    for (let k = 1; k < idx.length; k++) {
        if (idx[k] === prev + 1) { prev = idx[k]; continue; }
        runs.push([start, prev]); start = idx[k]; prev = idx[k];
    }
    runs.push([start, prev]);
    return runs.map(([a, b]) => (a === b ? order[a] : `${order[a]}-${order[b]}`));
}

// Same as collapseRanges but joined into a single string, e.g. "3rd-6th, 9U-12U".
export function summarizeRanges(values, order) {
    return collapseRanges(values, order).join(", ");
}

// Collapse a set of graduation years into contiguous dash ranges, e.g.
// ["2032","2033"] -> ["2032-2033"];  ["2032","2034"] -> ["2032","2034"].
export function collapseYearRanges(years) {
    const nums = [...new Set((years || []).map(Number).filter(Boolean))].sort((a, b) => a - b);
    if (!nums.length) return [];
    const order = [];
    for (let y = nums[0]; y <= nums[nums.length - 1]; y += 1) order.push(String(y));
    return collapseRanges(nums.map(String), order);
}

// Gender (B/G) + collapsed age/grade/grad-year summary for an event, e.g. "G · 10U-17U".
// Shows only what the creator entered (grade OR grad year, not an auto-expansion of both).
export function eventAudience(e) {
    const genders = e.genders || [];
    const g = [genders.includes("M") ? "B" : null, genders.includes("F") ? "G" : null].filter(Boolean).join("/");
    const grades = summarizeRanges(e.grades, GRADES);
    const ages = summarizeRanges(e.age_groups, agesForSport(e.sport));
    const gradYears = collapseYearRanges(e.grad_years).join(", ");
    const ag = [grades, ages, gradYears].filter(Boolean).join(", ");
    return [g, ag].filter(Boolean).join(" · ");
}

// Structured version of eventAudience: each segment tagged with whether the creator actually
// PROVIDED that dimension (vs. it being auto cross-mapped). Cards/detail bold the provided ones.
export function eventAudienceParts(e) {
    const genders = e.genders || [];
    const g = [genders.includes("M") ? "B" : null, genders.includes("F") ? "G" : null].filter(Boolean).join("/");
    const prov = new Set(e.provided_dims || []);
    const grades = summarizeRanges(e.grades, GRADES);
    const ages = summarizeRanges(e.age_groups, agesForSport(e.sport));
    const gradYears = collapseYearRanges(e.grad_years).join(", ");
    const parts = [];
    if (g) parts.push({ text: g, provided: false, gender: true });
    if (grades) parts.push({ text: grades, provided: prov.has("grades") });
    if (ages) parts.push({ text: ages, provided: prov.has("age_groups") });
    if (gradYears) parts.push({ text: gradYears, provided: prov.has("grad_years") });
    return parts;
}

// ---- Age ⇄ grade ⇄ grad-year cross-map (mirrors backend events _cross_fill_dims) ----
function _digits(s) { const n = String(s).replace(/\D/g, ""); return n ? parseInt(n, 10) : 0; }

export function seniorGradYear(today) {
    const d = today || new Date();
    return d.getFullYear() + (d.getMonth() >= 8 ? 1 : 0); // rolls up on Sept 1 (month index 8)
}

// Given the actually-entered ages/grades/grad-years, fill the other two dimensions by the
// deterministic mapping grade N ↔ (N+6)U ↔ (senior + 12 - N). Returns which dims were provided.
export function crossFillDims(agesIn, gradesIn, yearsIn, today) {
    const ages = (agesIn || []).filter(Boolean);
    const grades = (gradesIn || []).filter(Boolean);
    const years = (yearsIn || []).map((y) => String(y)).filter((y) => y.trim());
    const senior = seniorGradYear(today);
    const provided = [];
    const gradeNums = new Set();
    if (grades.length) { provided.push("grades"); grades.forEach((g) => gradeNums.add(_digits(g))); }
    if (ages.length) { provided.push("age_groups"); ages.forEach((a) => gradeNums.add(_digits(a) - 6)); }
    if (years.length) { provided.push("grad_years"); years.forEach((y) => { if (/^\d+$/.test(y)) gradeNums.add(12 - (parseInt(y, 10) - senior)); }); }
    const nums = [...gradeNums].filter((n) => n >= 2 && n <= 12).sort((a, b) => a - b);
    return {
        ages: provided.includes("age_groups") ? ages : nums.map((n) => `${n + 6}U`),
        grades: provided.includes("grades") ? grades : nums.map((n) => GRADES[n - 2]),
        grad_years: provided.includes("grad_years") ? years : nums.map((n) => String(senior + (12 - n))),
        provided,
    };
}

// Structured age/grade/grad-year summary for a TEAM PROGRAM (a set of rosters). Aggregates
// each roster's single identifier, cross-fills the other dimensions, and marks which dimension
// the user actually entered so the detail page can bold it (same convention as events).
export function programAudienceParts(rosters) {
    const list = rosters || [];
    const ages = [...new Set(list.map((r) => r.age_group).filter(Boolean))];
    const grades = [...new Set(list.map((r) => r.grade).filter(Boolean))];
    const years = [...new Set(list.map((r) => r.grad_year).filter(Boolean))];
    const filled = crossFillDims(ages, grades, years);
    const prov = new Set(filled.provided);
    const grStr = summarizeRanges(filled.grades, GRADES);
    const agStr = summarizeRanges(filled.ages, ALL_AGES);
    const yrStr = collapseYearRanges(filled.grad_years).join(", ");
    const parts = [];
    if (grStr) parts.push({ text: grStr, provided: prov.has("grades") });
    if (agStr) parts.push({ text: agStr, provided: prov.has("age_groups") });
    if (yrStr) parts.push({ text: yrStr, provided: prov.has("grad_years") });
    return parts;
}

